import { BtnLink, Form, MrtTable } from '@mi-front/ui';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Checkbox, FormControlLabel, FormGroup, Grid, Paper } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import { LDateTime } from '@app/components/date/LDateTime';
import { StationsSelect } from '@app/components/stations/StationsSelect';
import { ReportResultProps } from '@app/core/reports/types';
import { useStations, useStationsFinder } from '@app/core/stations/hooks/useStations';
import { StationLabel } from '@app/modules/admin/screens/stations/list/StationLabel';
import { gipReportResultMatchDateTimeColumnHeaderMessage } from '@app/modules/audit/screens/global-reports/gip/common/gip-report-result-intl-messages';
import { buildLoggerStreamUrl } from '@app/modules/logger/screens/stream/helpers/build-logger-stream-url';
import { DateTimeStr, Station } from '@mi-front/core';
import { GipAlternativeReportType } from '../../types';
import { useIncludeMatches } from './useIncludeMatches';

export type GipAltReportMatchesContentProps = Pick<
  ReportResultProps<GipAlternativeReportType>,
  'values' | 'result'
>;

/**
 * Station filter, export options, and matches table for the GIP alternative report.
 * Owns include-all / row-selection and station filter state (no prop drilling from parent).
 */
export function GipAltReportMatchesContent({
  report,
}: {
  report: GipAltReportMatchesContentProps;
}) {
  const { values, result } = report;
  const matches = result.matches;
  const { includeAll, toggleIncludeAll, setRowSelection, rowSelection } =
    useIncludeMatches(matches);

  const { $t } = useIntl();
  const stations = useStations();
  const finder = useStationsFinder();

  const filterStations = stations.filter(
    (s) => !values.stations?.length || !!values.stations?.find((_s) => s.id === _s.id),
  );

  const [filter, setFilter] = useState<{
    station: Station | null;
  }>({
    station: null,
  });

  const filteredMatches = matches.filter(
    (m) => !filter.station || m.stationId === filter.station.id,
  );

  const filterForm = (
    <Form
      initialValues={{
        station: null,
      }}
      validationSchema={yup.object().shape({})}
      onSubmit={(_data) => {
        // noop
      }}
      onChange={(data) => {
        setFilter({
          station: data.station,
        });
      }}
      actions={() => null}
    >
      {(_formik) => {
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={5}>
              <StationsSelect name={'station'} stations={filterStations} multiple={false} />
            </Grid>
          </Grid>
        );
      }}
    </Form>
  );

  return (
    <>
      <Paper sx={{ px: 2, mb: 2 }}>
        <div className={'pt-3 mb-3'}>
          {$t({
            id: 'audit.gipReport.resultOptions',
            defaultMessage: 'Opciones de resultados',
          })}
        </div>
        {filterForm}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeAll}
                onChange={(evt, checked) => toggleIncludeAll(checked)}
              />
            }
            label={$t({
              id: 'audit.gipReport.includeAllMatches',
              defaultMessage: 'Incluir todos los matches encontrados en el export',
            })}
          />
        </FormGroup>
      </Paper>

      <MrtTable
        data={filteredMatches}
        enableRowSelection={!includeAll}
        getRowId={(row) => row.id}
        onRowSelectionChange={setRowSelection}
        state={{ rowSelection }}
        initialState={{
          sorting: [{ id: 'datetime', desc: true }],
        }}
        columns={[
          {
            id: 'station',
            header: $t({
              id: 'audit.gipReport.result.station',
              defaultMessage: 'Emisora',
            }),
            size: 200,
            accessorFn: (item) => {
              return finder(item.stationId)?.name ?? item.stationId;
            },
            Cell: ({ row }) => {
              const stationId = row.original.stationId;
              const station = finder(stationId) ?? {
                id: stationId,
                name: stationId,
                slug: stationId,
              };
              return <StationLabel station={station} size="sm" singleLineEllipsis />;
            },
          },
          {
            header: $t(gipReportResultMatchDateTimeColumnHeaderMessage),
            size: 200,
            id: 'datetime',
            accessorFn: (item) => {
              return item.datetime;
            },
            Cell: ({ row }) => {
              const station = finder(row.original.stationId);

              return (
                <LDateTime
                  value={row.original.datetime as DateTimeStr}
                  showSeconds
                  tz={station?.timezone}
                />
              );
            },
          },
          {
            header: $t({
              id: 'audit.gipReport.result.advertiser',
              defaultMessage: 'Anunciante',
            }),
            accessorKey: 'metadata.advertiser',
            size: 200,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.campaign',
              defaultMessage: 'Campaña',
            }),
            accessorKey: 'metadata.campaign',
            size: 300,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.version',
              defaultMessage: 'Versión',
            }),
            accessorKey: 'metadata.version',
            size: 300,
          },
          {
            header: $t({
              id: 'audit.gipReport.columnCustomId',
              defaultMessage: 'Material',
            }),
            accessorKey: 'customId',
            size: 190,
          },
          {
            header: $t({
              id: 'audit.gipReport.result.order',
              defaultMessage: 'Orden',
            }),
            size: 180,
            accessorKey: 'metadata.order',
          },
          {
            header: $t({
              id: 'common.text.actions',
              defaultMessage: 'Acciones',
            }),
            enableSorting: false,
            enableColumnFilter: false,
            accessorFn: (e) => {
              const durationMs = (e.audio?.duration ?? 0) as number;
              const fromMs = moment(e.datetime).subtract(10, 'seconds').valueOf();
              const toMs = fromMs + (durationMs > 0 ? durationMs : 120 * 1000);
              const stationSlug = finder(e.stationId)?.slug ?? e.stationId;
              const url = buildLoggerStreamUrl(stationSlug, {
                selection: { from: fromMs, to: toMs },
              });
              return (
                <BtnLink size="xs" to={url} openInNewTab icon={<OpenInNewIcon />} className="mr-2">
                  {$t({
                    id: 'common.show',
                    defaultMessage: 'Mostrar',
                  })}
                </BtnLink>
              );
            },
          },
        ]}
      />
    </>
  );
}
